<template>
  <div>
    <b-dropdown
      id="other-options"
      toggle-tag="div"
      toggle-class="p-0 bg-transparent"
      menu-class="mc-fleet-task-other-options__menu"
    >
      <template #button-content>
        <McButton
          class="mc-fleet-task-other-options__trigger"
          variant="secondary"
          icon="icon-outline_chevron-down"
          icon-position="right"
          :text="$t('global.object.other')"
          :screen-breakpoint="screenBreakpoint"
        />
      </template>

      <b-dropdown-item
        v-if="
          ![
            'Draf',
            'Temporary',
            'Menunggu Vendor',
            'Diterima Vendor',
            'Ditolak',
            'Dibatalkan',
          ].includes(fleetTaskStatus)
        "
        @click="handlePrintPDF"
      >
        {{ $t('fleetTask.printOrder') }}
      </b-dropdown-item>
      <b-dropdown-item
        v-if="hasSamatorPrintAccess && hasAllLiquidProductType"
        @click="handlePrintSamatorPdf('SPK')"
      >
        {{ $t('fleetTask.otherOptions.printConsolidatedShipmentEpicor') }}
      </b-dropdown-item>
      <b-dropdown-item v-if="hasSamatorPrintAccess" @click="handlePrintSamatorPdf('SJ')">
        {{ $t('fleetTask.otherOptions.printTravelDocumentEpicor') }}
      </b-dropdown-item>
      <b-dropdown-item v-if="fleetTaskStatus != 'Temporary'" @click="handleShowModal">
        {{ $t('fleetTask.fleetTaskHistory') }}
      </b-dropdown-item>
      <b-dropdown-item
        v-if="hasAccessAll(['webhook-log-event-ft:read'])"
        @click="$bvModal.show('integration-log-modal-value-help')"
      >
        {{ $t('fleetTask.otherOptions.integrationHistory') }}
      </b-dropdown-item>
    </b-dropdown>
    <McEventList
      :value.sync="modal"
      :data="groupedFleetHistories"
      :keys="groupedFleetHistoryKeys"
      :title="$t('fleetTask.fleetTaskHistory')"
      @itemClick="handleShowDetailHistory"
    />

    <DeliveryLoadHistoryDetail :data="selectedHistory" />

    <BulkDeliveryOrderHistoryDetail :data="selectedBulkDOHistory" />

    <IntegrationLogHistoryModal
      v-if="hasAccessAll(['webhook-log-event-ft:read'])"
      module="fleet-task"
      :identifier="$route.params.id"
      :hasCustomFeature="true"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Helper } from '@/mixin/helper'

import McButton from '@/components/McButton'
import McEventList from '@/components/McEventList'
import DeliveryLoadHistoryDetail from './DeliveryLoadHistoryDetail'
import BulkDeliveryOrderHistoryDetail from './BulkDeliveryOrderHistoryDetail'
import IntegrationLogHistoryModal from '@/router/views/master/fleet/components/IntegrationLogHistoryModal'

import * as FleetHistory from '@/services/rest-api/lib/fleet-task/history'
import * as Samator from '@/services/rest-api/lib/fleet-task/samator'
import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    McButton,
    McEventList,
    DeliveryLoadHistoryDetail,
    BulkDeliveryOrderHistoryDetail,
    IntegrationLogHistoryModal,
  },
  mixins: [Helper],
  data() {
    return {
      modal: false,
      groupedFleetHistories: [],
      groupedFleetHistoryKeys: [],
      selectedHistory: {},
      selectedBulkDOHistory: {},
      documentSamator: {
        SJ: 'Surat Jalan',
        SPK: 'Consolidated Shipment',
      },
      hasAllLiquidProductType: false,
      liquidProductType: ['BULK', 'TUBE'],
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapState('fleetTask', ['fleetTaskStatus', 'fleetTaskDetail', 'sequence']),
    getFleetHistory() {
      return FleetHistory.get(this.$route.params.id)
    },
    hasBinNumberSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data?.company_id?.includes(this.getCompanyId())
    },
    hasSamatorPrintAccess() {
      return (
        this.hasBinNumberSamator &&
        ['Dijadwalkan', 'Perjalanan', 'Selesai'].includes(this.fleetTaskStatus) &&
        this.fleetTaskDetail?.shipment_reference !== null
      )
    },
    screenBreakpoint() {
      const largeBreakpointStatuses = ['Selesai', 'Dibatalkan', 'Ditolak']

      return largeBreakpointStatuses.includes(this.fleetTaskStatus) ? null : 768
    },
  },
  watch: {
    sequence: {
      handler() {
        this.validateAllLiquidProducts()
      },
      deep: true,
    },
  },
  mounted() {
    this.validateAllLiquidProducts()
  },
  methods: {
    handlePrintPDF() {
      window.open(
        process.env.VUE_APP_API_FLEET_PLANNING_URL + `/fleet-task/${this.$route.params.id}/pdf`
      )
    },
    async handleShowModal() {
      this.modal = true
      const queryParam = {
        status: 'basic',
      }

      const { data, errors } = await this.getFleetHistory(queryParam)

      if (errors) return
      const fleetHistory = data

      this.groupedFleetHistories = []
      this.groupedFleetHistoryKeys = []

      let oneDayObject = {}
      let currentDate = moment().startOf('day')
      let currentTimeText = ''

      const deliveryOrderMap = {
        transferred_delivery_orders_added: {
          title: this.$t('fleetTask.otherOptions.bulkHistory.added.title'),
          label: this.$t('fleetTask.otherOptions.bulkHistory.added.label'),
        },
        transferred_delivery_orders_moved: {
          title: this.$t('fleetTask.otherOptions.bulkHistory.moved.title'),
          label: this.$t('fleetTask.otherOptions.bulkHistory.moved.label'),
        },
        assigned_delivery_orders: {
          title: this.$t('fleetTask.otherOptions.bulkHistory.assigned.title'),
          label: this.$t('fleetTask.otherOptions.bulkHistory.assigned.label'),
        },
        canceled_delivery_orders: {
          title: this.$t('fleetTask.otherOptions.bulkHistory.canceled.title'),
          label: this.$t('fleetTask.otherOptions.bulkHistory.canceled.label'),
        },
        rescheduled_orders: {
          title: this.$t('fleetTask.otherOptions.bulkHistory.rescheduled.title'),
          label: this.$t('fleetTask.otherOptions.bulkHistory.rescheduled.label'),
        },
      }
      const deliveryOrderKeys = [
        'transferred_delivery_orders',
        'assigned_delivery_orders',
        'canceled_delivery_orders',
        'rescheduled_orders',
      ]

      fleetHistory.forEach((item, index) => {
        const isTransferTitleMoved = item.title?.toLowerCase().includes('dipindahkan')

        const idxBulkHistory = item.properties.findIndex((prop) =>
          deliveryOrderKeys.includes(prop.key)
        )

        if (idxBulkHistory !== -1) {
          const prop = item.properties[idxBulkHistory]

          if (prop.key === 'transferred_delivery_orders') {
            prop.key = isTransferTitleMoved
              ? 'transferred_delivery_orders_moved'
              : 'transferred_delivery_orders_added'
          }

          const config = deliveryOrderMap[prop.key]

          if (prop.value?.length > 3) {
            item = {
              ...item,
              index: item.created_on,
              clickable: true,
              clickableTitle: this.$t('fleetTask.otherOptions.seeDoDetail'),
              isBulk: true,
              modalTitle: config.title,
              labelCreatedOn: config.label,
              delivery_orders: item.properties[idxBulkHistory].value,
            }
          }

          item.properties.splice(idxBulkHistory, 1)
        } else if (item.title.toLowerCase().includes('perubahan kuantitas muatan'.toLowerCase())) {
          item.clickable = true
          item.clickableTitle = this.$t('fleetTask.otherOptions.seeChange')
          const typeMatch = item.title.match(/\(([^)]+)\)/)
          item.type = typeMatch ? typeMatch[1] : ''
          item.index = index
          if (item.description.toLowerCase().includes('kuantitas do-'.toLowerCase())) {
            item.do_number = item.description.match(/DO-\d+/i)?.[0] || ''
          }

          for (let i = 0; i < item.properties.length; i++) {
            if (item.properties[i].key === 'delivery_order_uuid') {
              item.delivery_order_uuid = item.properties[i].value
              item.properties.splice(i, 1)
              break
            }
          }

          item.filter = {
            type: item.type,
            updated_by: item.properties.find((prop) => prop.key === 'Diubah oleh')?.value || '',
            created_on: item.created_on,
          }
        } else if (
          item.title.toLowerCase().includes('Penugasan mulai dijalankan'.toLowerCase()) &&
          item.properties.some((p) => p.value === 'Fleet Task Auto Start')
        ) {
          item.properties = item.properties.filter((p) => p.value !== 'Fleet Task Auto Start')
          item.hasBadge = true
          item.badgeText = this.$t('fleetTask.otherOptions.automatic')
          item.badgeVariant = 'outlineSuccess'
          item.badgeIcon = 'icon-fill_flash'
        }

        const createdOnItem = moment(item.created_on).startOf('day')
        const timeDiff = createdOnItem.diff(currentDate, 'days')

        if (timeDiff === 0) {
          if (index !== 0) oneDayObject[currentTimeText].push(item)
          else {
            currentTimeText = this.$t('fleetTask.otherOptions.today')
            oneDayObject[currentTimeText] = []
            oneDayObject[currentTimeText].push(item)
          }
        } else {
          if (index !== 0) {
            this.groupedFleetHistories.push({ [currentTimeText]: oneDayObject[currentTimeText] })
            this.groupedFleetHistoryKeys.push(currentTimeText)
          }

          currentDate = createdOnItem
          currentTimeText = createdOnItem.format('DD MMM YYYY')

          oneDayObject[currentTimeText] = []
          oneDayObject[currentTimeText].push(item)
        }

        if (fleetHistory.length - 1 === index) {
          this.groupedFleetHistories.push({ [currentTimeText]: oneDayObject[currentTimeText] })
          this.groupedFleetHistoryKeys.push(currentTimeText)
        }
      })
    },
    handleShowDetailHistory(item) {
      if (item.title.toLowerCase().includes('perubahan kuantitas muatan'.toLowerCase())) {
        this.selectedHistory = item
        this.$nextTick(() => {
          this.$bvModal.show(`mc-modal-load-history-${item.index}`)
        })
      } else if (item.isBulk) {
        this.selectedBulkDOHistory = item
        this.$nextTick(() => {
          this.$bvModal.show(`mc-modal-bulk-do-history-${item.index}`)
        })
      }
    },
    async handlePrintSamatorPdf(type) {
      try {
        const res = await Samator.print(type, this.fleetTaskDetail.shipment_reference)

        if (res?.data?.base64 === '' || res?.data?.base64 === null) {
          this.$mcToast.error(this.$t('fleetTask.otherOptions.documentNotFound'))
          return
        }

        if (res.errors) {
          this.$mcToast.error(this.$t('fleetTask.otherOptions.downloadDocumentFailed'))
          return
        }

        const blob = this.base64ToBlob(res.data.base64, 'application/pdf')
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()

        URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        this.$mcToast.error(this.$t('fleetTask.otherOptions.downloadDocumentFailed'))
      }
    },
    base64ToBlob(base64String, mimeType = 'application/pdf') {
      const byteCharacters = atob(base64String) // Decode base64
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      return new Blob([byteArray], { type: mimeType })
    },
    handleShowModalHistoryMgm() {
      this.$bvModal.show('mc-modal-history-mgm')
    },
    async validateAllLiquidProducts() {
      try {
        const defaultPoints = this.sequence?.filter((point) => point?.type === 'DEFAULT')

        if (!defaultPoints || defaultPoints.length === 0) {
          this.hasAllLiquidProductType = false
          return
        }

        // Get the first default point
        const firstPoint = defaultPoints[0]

        if (!firstPoint?.activities || firstPoint.activities.length === 0) {
          this.hasAllLiquidProductType = false
          return
        }

        // Get the first activity
        const firstActivity = firstPoint.activities[0]

        // Get delivery order ID from the first activity
        const deliveryOrderId = firstActivity?.delivery_order?.id
        if (!deliveryOrderId) {
          this.hasAllLiquidProductType = false
          return
        }

        // Fetch the delivery order details
        const { data, errors } = await DeliveryOrder.show(deliveryOrderId)

        if (errors || !data) {
          this.hasAllLiquidProductType = false
          return
        }

        // Find the UDF with name "Product Type"
        const productTypeUdf = data.user_definition_fields?.find(
          (udf) => udf.name.toLowerCase() === 'product type'
        )

        // Check if the value is "Liquid"
        this.hasAllLiquidProductType = this.liquidProductType.includes(
          productTypeUdf?.value?.toUpperCase()
        )
      } catch (error) {
        console.error('Error validating liquid products:', error)
        this.hasAllLiquidProductType = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(#other-options) {
  .dropdown-menu {
    width: max-content;
    min-width: 220px;
    max-width: min(calc(100vw - 32px), 320px);
  }

  .dropdown-toggle {
    border: none;
  }

  .dropdown-item {
    white-space: normal;
    word-break: break-word;
  }

  .mc-fleet-task-other-options__trigger .tooltip {
    display: none !important;
  }
}

@media screen and (max-width: 767px) {
  :deep(#other-options) {
    .dropdown-menu {
      left: 0 !important;
      right: auto !important;
    }
  }
}
</style>
